/**
 * Inlined replacement for @googlemaps/js-api-loader — that package ships
 * ES2020+ in its CJS build and webpack 4 (v2-consumer) can't parse it.
 * Wraps google.maps.importLibrary on the `quarterly` channel.
 */

let bootstrapped = false;

const isBrowser = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const bootstrap = ({ key, v }) => {
  const w = window;
  const d = document;
  const ns = w.google || (w.google = {});
  const maps = ns.maps || (ns.maps = {});

  if (maps.importLibrary) return;

  const requested = new Set();
  let scriptInjected = false;
  let resolveLoad;
  let rejectLoad;
  const loadPromise = new Promise((resolve, reject) => {
    resolveLoad = resolve;
    rejectLoad = reject;
  });

  maps.importLibrary = libraryName => {
    requested.add(libraryName);
    if (!scriptInjected) {
      scriptInjected = true;
      // `__ib__` is Google Maps' internal bootstrap callback name — the JSONP
      // hook the maps API CDN will invoke on load. The string must match the
      // `callback` URL param exactly; this is Google's wire contract, not ours.
      const params = new URLSearchParams({
        key,
        v,
        libraries: Array.from(requested).join(','),
        callback: 'google.maps.__ib__',
      });
      const script = d.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.onerror = () =>
        rejectLoad(new Error('The Google Maps JavaScript API could not load.'));
      // eslint-disable-next-line no-underscore-dangle
      maps.__ib__ = resolveLoad;
      d.head.appendChild(script);
    }
    return loadPromise.then(() => maps.importLibrary(libraryName));
  };
};

export const setOptions = options => {
  if (!isBrowser() || bootstrapped) return;
  bootstrapped = true;
  bootstrap(options);
};

export const importLibrary = libraryName => {
  if (!isBrowser()) {
    return Promise.reject(
      new Error('Google Maps cannot load outside a browser environment.')
    );
  }
  if (
    !window.google ||
    !window.google.maps ||
    !window.google.maps.importLibrary
  ) {
    return Promise.reject(
      new Error('Call setOptions() before importLibrary().')
    );
  }
  return window.google.maps.importLibrary(libraryName);
};
